// Bolo3K bootstrap — mobile/touch client scaffold. Reads room+name from the
// URL, connects to the same authoritative server as the desktop client, and
// runs the client-core render/predict loop on a full-viewport canvas.
//
// Wave 2b modules integrate at the MODULE INTEGRATION POINT below: each
// module exports an init function taking (ctx: GameContext, mount: HTMLElement)
// and owns exactly one mount-point div (see client3k.md for the map).

import { BOT_PERSONALITIES, type BotPersonality } from '@bolo2k/shared';
import { LGMStatus, type BuildActionCode, type BuildCommand } from '@bolo2k/sim';
import {
  applySnapshot,
  Audio,
  Connection,
  findMyTank,
  initFromWelcome,
  Interpolator,
  loadSprites,
  playerLabel,
  Predictor,
  Renderer,
  TILE_PX,
  WireEvents,
  type ClientState,
  type ClientTank,
} from '@bolo2k/client-core';
import type { GameContext } from './context.js';
import type { InputSource } from './input/inputSource.js';
import { KeyboardFallbackInput } from './input/keyboardFallback.js';
import { initTouchControls } from './input/touchControls.js';
import { initMobileHud, type MobileHudApi } from './hud/mobileHud.js';
import { initComms, type CommsApi } from './comms/comms.js';
import { haptic, initPwa } from './pwa/index.js';
import { initMobileLobby, PENDING_BOTS_STORAGE_KEY } from './lobby/mobileLobby.js';
import { hitmapRequested, initHitmap } from './debug/hitmap.js';

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} not found`);
  return el;
}

const statusLine = $('status-line');
// Until the HUD mounts, status goes to the scaffold's plain status line;
// after that, everything routes to HUD toasts (setToastSink flips it).
let toastSink: ((text: string, ms?: number) => void) | null = null;
function setStatus(msg: string): void {
  if (toastSink) toastSink(msg);
  else statusLine.textContent = msg;
}
function setToastSink(sink: (text: string, ms?: number) => void): void {
  toastSink = sink;
  statusLine.hidden = true;
}

// ---------------------------------------------------------------------------
// Join flow. room+name live in the URL query so a reload (or a PWA relaunch)
// drops straight back into the battle. Without them, the mobile lobby
// (src/lobby/) takes over #lobby-root and ends the same way: reload with
// ?room=…&name=… set.
// ---------------------------------------------------------------------------

const params = new URLSearchParams(location.search);
const roomParam = params.get('room');
const nameParam = params.get('name') ?? '';

if (!roomParam) {
  initMobileLobby($('lobby-root'));
} else {
  startGame(roomParam, nameParam);
}

// ---------------------------------------------------------------------------
// Game proper.
// ---------------------------------------------------------------------------

function wsURL(roomId: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws?room=${encodeURIComponent(roomId)}`;
}

function startGame(roomId: string, name: string): void {
  const stage = $('stage') as HTMLCanvasElement;
  const stageWrap = $('stage-wrap');

  const renderer = new Renderer(stage);
  const audio = new Audio();
  const interpolator = new Interpolator();
  const predictor = new Predictor();

  // Browsers create AudioContexts suspended until a user gesture — enable on
  // the first pointerdown (touch or mouse); keydown too for desktop testing.
  const enableAudio = () => {
    audio.enable();
    window.removeEventListener('pointerdown', enableAudio);
    window.removeEventListener('keydown', enableAudio);
  };
  window.addEventListener('pointerdown', enableAudio);
  window.addEventListener('keydown', enableAudio);

  // Resize: observe the WRAPPER, not the canvas — the canvas is sized 1 CSS
  // pixel larger for the renderer's sub-pixel scroll smoothing, so observing
  // it directly would feed back a size that's off by one. Renderer handles
  // devicePixelRatio internally in resize().
  const fitCanvas = () => {
    const rect = stageWrap.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) renderer.resize(rect.width, rect.height);
  };
  fitCanvas();
  new ResizeObserver(fitCanvas).observe(stageWrap);

  // Sprites load in parallel with the connection; invalidate the terrain
  // cache when they land so the next frame repaints with real art.
  loadSprites()
    .then(() => renderer.invalidateTerrainCache())
    .catch((e) => setStatus(`sprite load failed: ${String(e)}`));

  let state: ClientState | null = null;
  let lastInputBits = -1;

  // Active input provider — keyboard fallback until Wave 2b-1's touch
  // controls call ctx.setInputSource().
  let inputSource: InputSource = new KeyboardFallbackInput();

  // Armed-build handshake (HUD arms → stage tap targets → queued command).
  let armedAction: BuildActionCode | null = null;
  // Latest-wins queue, held until the LGM is home (the server silently drops
  // build input while the LGM is out — same gate the desktop client applies).
  let queuedBuild: BuildCommand | null = null;

  stage.addEventListener('pointerdown', (e) => {
    if (armedAction === null || !state) return;
    const rect = stage.getBoundingClientRect();
    const cam = renderer.cameraTile(); // viewport top-left in tile units
    const tx = Math.floor(cam.x + (e.clientX - rect.left) / TILE_PX);
    const ty = Math.floor(cam.y + (e.clientY - rect.top) / TILE_PX);
    if (tx < 0 || tx > 255 || ty < 0 || ty > 255) return;
    queuedBuild = { action: armedAction, targetX: tx, targetY: ty };
    armedAction = null;
    setStatus(`build queued at ${tx},${ty}`);
  });

  // Modules mount right after the context is built below; the connection's
  // callbacks fire on ws events, long after both exist — the optional
  // chaining is for the type system, not a real startup race.
  let hud: MobileHudApi | undefined;
  let comms: CommsApi | undefined;
  // Map-event news lines (base lost, pillbox captured, …) go to the comms
  // feed's system channel — Bolo3K's stand-in for the desktop news wire.
  const wire = new WireEvents({ add: (text) => comms?.showSystem(text) });
  // Haptic tracking: armour drops and deaths buzz on supported hardware.
  let prevArmour = -1;
  let prevAlive = true;

  setStatus(`connecting to room ${roomId}…`);
  const conn = new Connection(
    wsURL(roomId),
    {
      onWelcome: (welcome) => {
        state = initFromWelcome(welcome);
        wire.reset();
        prevArmour = -1;
        prevAlive = true;
        setStatus(
          welcome.owner < 0
            ? `spectating "${welcome.mapName}"`
            : `connected as ${name || `player ${welcome.owner}`} on "${welcome.mapName}"`,
        );
        // Replay bot picks stashed by the create-battle screen (the join is a
        // page reload, so the lobby can't send them itself). Key is deleted
        // first so a mid-game reload never re-adds bots.
        if (welcome.owner >= 0) {
          const raw = sessionStorage.getItem(PENDING_BOTS_STORAGE_KEY);
          if (raw) {
            sessionStorage.removeItem(PENDING_BOTS_STORAGE_KEY);
            try {
              const picks = JSON.parse(raw) as unknown;
              if (Array.isArray(picks)) {
                for (const p of picks) {
                  if ((BOT_PERSONALITIES as readonly string[]).includes(String(p))) {
                    conn.addBot(p as BotPersonality);
                  }
                }
              }
            } catch {
              // Malformed stash — ignore; the player can add bots in-game later.
            }
          }
        }
      },
      onSnapshot: (snap) => {
        if (!state) return;
        applySnapshot(state, snap);
        interpolator.recordSnapshot(state);
        predictor.reconcile(state);
        // Minimal audio: play the server's one-shot sound events (fresh in
        // every snapshot; server clears after broadcast — no dedup needed).
        const me = findMyTank(state);
        const listener = me ? { x: me.x, y: me.y } : null;
        for (const ev of snap.soundEvents) audio.play(ev.kind, ev.x, ev.y, listener);
        wire.observe(snap);
        if (me) {
          if (prevAlive && !me.alive) haptic('death');
          else if (prevArmour >= 0 && me.armour < prevArmour) haptic('hit');
          prevArmour = me.armour;
          prevAlive = me.alive;
        }
      },
      onMessage: (msg) => {
        if (msg.type === 's2c.error') setStatus(`server: ${msg.message}`);
        // The server echoes the sender's own chat back, so routing every
        // s2c.chat through the feed (self included) shows exactly one copy.
        if (msg.type === 's2c.chat' && state) {
          comms?.showIncoming(playerLabel(state, msg.from), msg.text, msg.scope);
        }
      },
      onClose: (code, reason) => setStatus(`disconnected (${code}${reason ? ` ${reason}` : ''})`),
      onError: (err) => setStatus(`network error: ${err.message}`),
      onReconnecting: (attempt, delayMs) => setStatus(`reconnecting (attempt ${attempt} in ${delayMs} ms)…`),
      onReconnected: () => {
        setStatus('reconnected');
        lastInputBits = -1;
      },
      onGiveUp: () => setStatus('disconnected — reload to reconnect'),
    },
    undefined,
    { name },
  );

  const ctx: GameContext = {
    state: () => state,
    myTank: (): ClientTank | null => (state ? (findMyTank(state) ?? null) : null),
    connection: conn,
    renderer,
    audio,
    setInputSource: (src) => {
      if (src === inputSource) return;
      inputSource.dispose();
      inputSource = src;
    },
    armBuildAction: (action) => {
      armedAction = action;
    },
    armedBuildAction: () => armedAction,
    sendChat: (text, scope) => conn.sendChat(text, scope),
    requestAlly: (target) => conn.requestAlly(target),
    cancelAlly: (target) => conn.cancelAlly(target),
  };

  // ------------------- MODULE INTEGRATION POINT -------------------
  hud = initMobileHud(ctx, $('hud-root'));
  setToastSink((text, ms) => hud?.showToast(text, ms));
  comms = initComms(ctx, $('comms-root'));

  // Touch controls own input on coarse-pointer devices; ?touch=1/0 overrides
  // (handy for desktop preview testing and for tablets with keyboards).
  const touchParam = params.get('touch');
  const useTouch =
    touchParam === '1' ||
    (touchParam !== '0' &&
      (matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window));
  if (useTouch) initTouchControls(ctx, $('controls-root'));

  initPwa({ isGameActive: () => state !== null });
  if (hitmapRequested()) initHitmap(); // ?hitmap=1 — tap-coverage debug overlay
  // -----------------------------------------------------------------

  function frame(): void {
    hud?.update();
    if (state) {
      const bits = inputSource.readKeys();
      const predictedMe = predictor.predict(state, { keys: bits });
      renderer.draw(state, interpolator, undefined, predictedMe, null);

      // Pull any one-shot build from the input source (touch controls emit
      // these directly); stage-tap targeting fills queuedBuild above.
      const srcBuild = inputSource.consumeBuild();
      if (srcBuild) queuedBuild = srcBuild;

      const me = findMyTank(state);
      const lgmFree = !!me && me.lgmStatus === LGMStatus.InTank;
      if (queuedBuild && me && lgmFree) {
        conn.sendInput(bits, queuedBuild);
        queuedBuild = null;
        lastInputBits = -1; // re-send plain keys next frame too
      } else if (bits !== lastInputBits && me) {
        // Send only on change — the server re-applies the latest input every
        // tick anyway.
        conn.sendInput(bits);
        lastInputBits = bits;
      }
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}
